import React, { useEffect, useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Download, QrCode, Save, Trophy, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { RANKING_START_SETTING_KEY, RANKING_TEAMS, buildRankingRows, loadRankingData } from '@/lib/rankingMBV';
import { exportCsvFile } from '@/utils/csvExport';

const initials = (name) => String(name || 'MBV').split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();
const checkinDateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Sao_Paulo'
});

const formatCheckinDateTime = (checkin) => {
  const value = checkin.checked_at || checkin.created_date;
  if (!value) return 'Data e hora não registradas';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Data e hora não registradas';
  return checkinDateFormatter.format(date).replace(', ', ' às ');
};

export default function AdminRankingMBV() {
  const queryClient = useQueryClient();
  const [activeTeamKey, setActiveTeamKey] = useState(RANKING_TEAMS[0].key);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const [rankingStartDate, setRankingStartDate] = useState('');

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data, isLoading } = useQuery({
    queryKey: ['rankingMBVData'],
    queryFn: loadRankingData
  });

  useEffect(() => {
    if (data) setRankingStartDate(data.rankingStartDate || '');
  }, [data]);

  const rankingByTeam = useMemo(() => {
    if (!data) return {};
    return RANKING_TEAMS.reduce((acc, team) => {
      const teamData = data.teams[team.key] || {};
      acc[team.key] = buildRankingRows({
        team,
        members: teamData.members || [],
        schedules: teamData.schedules || [],
        checkins: data.checkins || [],
        adjustments: data.adjustments || [],
        churchById: data.churchById
      });
      return acc;
    }, {});
  }, [data]);

  const activeTeam = RANKING_TEAMS.find((team) => team.key === activeTeamKey) || RANKING_TEAMS[0];
  const activeRows = rankingByTeam[activeTeamKey] || [];
  const selectedMember = activeRows.find((row) => row.id === selectedMemberId);

  const adjustmentMutation = useMutation({
    mutationFn: async () => {
      const parsedPoints = Number(points);
      if (!selectedMember) throw new Error('Selecione um integrante.');
      if (!Number.isFinite(parsedPoints) || parsedPoints === 0) throw new Error('Informe uma pontuação válida.');
      if (!reason.trim()) throw new Error('Informe o motivo da correção.');

      const created = await base44.entities.RankingMBVAdjustment.create({
        team_key: activeTeam.key,
        team_label: activeTeam.label,
        member_id: selectedMember.member_id,
        team_member_id: selectedMember.id,
        email: selectedMember.email,
        name: selectedMember.name,
        points: parsedPoints,
        reason: reason.trim(),
        created_by: user?.email || ''
      });

      await base44.entities.RankingMBVAdminLog.create({
        action: 'points_adjusted',
        actor_email: user?.email || '',
        team_key: activeTeam.key,
        team_member_id: selectedMember.id,
        points: parsedPoints,
        reason: reason.trim(),
        created_at: new Date().toISOString()
      }).catch(() => null);

      return created;
    },
    onSuccess: () => {
      toast.success('Pontuação corrigida.');
      setPoints('');
      setReason('');
      queryClient.invalidateQueries({ queryKey: ['rankingMBVData'] });
    },
    onError: (error) => toast.error(error?.message || 'Não foi possível corrigir a pontuação.')
  });

  const rankingStartMutation = useMutation({
    mutationFn: async () => {
      const value = rankingStartDate || '';
      const existingSetting = data?.rankingStartSetting;
      const payload = {
        key: RANKING_START_SETTING_KEY,
        value,
        start_date: value,
        label: 'Data oficial de início do Ranking MBV',
        updated_by: user?.email || ''
      };

      const saved = existingSetting?.id
        ? await base44.entities.RankingMBVSetting.update(existingSetting.id, payload)
        : await base44.entities.RankingMBVSetting.create(payload);

      await base44.entities.RankingMBVAdminLog.create({
        action: 'ranking_start_date_updated',
        actor_email: user?.email || '',
        start_date: value,
        created_at: new Date().toISOString()
      }).catch(() => null);

      return saved;
    },
    onSuccess: () => {
      toast.success(rankingStartDate
        ? 'Data oficial do ranking atualizada.'
        : 'Data oficial removida. Todo o histórico voltou a contar.');
      queryClient.invalidateQueries({ queryKey: ['rankingMBVData'] });
    },
    onError: (error) => toast.error(error?.message || 'Não foi possível atualizar a data oficial do ranking.')
  });

  const exportCsv = async () => {
    const rows = [['Equipe', 'Posição', 'Nome', 'Total MBV', 'Escalas', 'Confirmações', 'Check-ins']];
    RANKING_TEAMS.forEach((team) => {
      (rankingByTeam[team.key] || []).forEach((row, index) => {
        rows.push([team.label, index + 1, row.name, row.total, row.scheduledCount, row.confirmedCount, row.checkinCount]);
      });
    });
    try {
      await exportCsvFile(rows, 'ranking-mbv.csv');
      toast.success('Relatório gerado.');
    } catch (error) {
      toast.error(error?.message || 'Não foi possível gerar o relatório.');
    }
  };

  if (isLoading) {
    return <div className="min-h-screen bg-gray-50 p-4"><LoadingSpinner /></div>;
  }

  const totalMembers = Object.values(rankingByTeam).reduce((sum, rows) => sum + rows.length, 0);
  const totalCheckins = data?.checkins?.length || 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-slate-900 to-indigo-900 text-white pt-8 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          <PageHeader title="Ranking MBV" subtitle="Gestão profissional de pontuação e presença" showBack={true} />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <Users className="w-5 h-5 text-indigo-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{totalMembers}</p>
            <p className="text-sm text-gray-500">Integrantes ranqueados</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <Trophy className="w-5 h-5 text-amber-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{Math.max(0, ...Object.values(rankingByTeam).flat().map((row) => row.total))}</p>
            <p className="text-sm text-gray-500">Maior pontuação</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <QrCode className="w-5 h-5 text-emerald-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{totalCheckins}</p>
            <p className="text-sm text-gray-500">Check-ins registrados</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <Button variant="outline" onClick={exportCsv}>
            <Download className="w-4 h-4 mr-2" />
            Exportar relatórios
          </Button>
          <Link to={createPageUrl('AdminQRCodeMBV')}>
            <Button className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto">
              <QrCode className="w-4 h-4 mr-2" />
              Gerenciar QR Code
            </Button>
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-4 mb-5">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
                <h2 className="font-bold text-gray-900">Início oficial do ranking</h2>
              </div>
              <p className="text-sm text-gray-600">
                Tudo antes desta data fica fora da pontuação. O histórico não é apagado.
              </p>
            </div>
            <div className="w-full lg:w-56">
              <Label>Começar contagem em</Label>
              <Input
                type="date"
                value={rankingStartDate}
                onChange={(event) => setRankingStartDate(event.target.value)}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setRankingStartDate('')}
                disabled={rankingStartMutation.isPending}
              >
                Limpar data
              </Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => rankingStartMutation.mutate()}
                disabled={rankingStartMutation.isPending}
              >
                <Save className="w-4 h-4 mr-2" />
                Salvar início
              </Button>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-5">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <Tabs value={activeTeamKey} onValueChange={(value) => {
              setActiveTeamKey(value);
              setSelectedMemberId('');
            }}>
              <TabsList className="w-full h-auto flex flex-wrap justify-start mb-4">
                {RANKING_TEAMS.map((team) => (
                  <TabsTrigger key={team.key} value={team.key} className="flex-1 min-w-fit">
                    {team.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {RANKING_TEAMS.map((team) => (
                <TabsContent key={team.key} value={team.key}>
                  <div className="overflow-hidden rounded-xl border">
                    {(rankingByTeam[team.key] || []).map((row, index) => (
                      <div key={row.id} className="grid grid-cols-[44px_1fr_auto] items-center gap-3 p-3 border-b last:border-b-0">
                        <div className="font-bold text-gray-500">#{index + 1}</div>
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={row.photo_url} />
                            <AvatarFallback>{initials(row.name)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{row.name}</p>
                            <p className="text-xs text-gray-500">{row.scheduledCount} escalas · {row.confirmedCount} confirmações · {row.checkinCount} check-ins</p>
                          </div>
                        </div>
                        <Badge className="bg-indigo-600">{row.total} MBV</Badge>
                      </div>
                    ))}
                    {(rankingByTeam[team.key] || []).length === 0 && <div className="p-6 text-center text-gray-500">Sem integrantes.</div>}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          <aside className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <h2 className="font-bold text-gray-900 mb-4">Corrigir pontuação</h2>
              <div className="space-y-3">
                <div>
                  <Label>Integrante</Label>
                  <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeRows.map((row) => (
                        <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pontos</Label>
                  <Input type="number" value={points} onChange={(event) => setPoints(event.target.value)} placeholder="Ex: 5 ou -2" />
                </div>
                <div>
                  <Label>Motivo</Label>
                  <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Correção administrativa" />
                </div>
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => adjustmentMutation.mutate()}>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar correção
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <h2 className="font-bold text-gray-900">Check-ins recentes</h2>
              </div>
              {(data?.checkins || []).slice(0, 8).map((checkin) => (
                <div key={checkin.id} className="p-3 border-b last:border-b-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-gray-900 truncate">{checkin.name}</p>
                    <Badge className="bg-emerald-600">+{checkin.points}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{checkin.team_label} · {checkin.schedule_title}</p>
                  <p className="text-xs text-gray-600 mt-1">Check-in: {formatCheckinDateTime(checkin)}</p>
                </div>
              ))}
              {(data?.checkins || []).length === 0 && <div className="p-5 text-center text-sm text-gray-500">Nenhum check-in ainda.</div>}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
